import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Plus, TrendingUp, ArrowRight, MoreVertical, X, User, Calendar, ChevronDown, Check } from 'lucide-react';

const AdminDiscountsCreditsPage = () => {
  const [activeTab, setActiveTab] = useState('codes');
  const [isIssueCreditOpen, setIsIssueCreditOpen] = useState(false);
  const [isEditDiscountOpen, setIsEditDiscountOpen] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [discountCodes, setDiscountCodes] = useState<any[]>([]);
  const [manualCredits, setManualCredits] = useState<any[]>([]);
  const [discountMetrics, setDiscountMetrics] = useState({ activeCodes: 0, totalRedeemed: 0, totalValue: '£0.00', conversionRate: '0%' });
  const [creditsMetrics, setCreditsMetrics] = useState({ totalIssued: '£0.00', activeBalance: '£0.00', totalUsed: '£0.00' });
  const [redemptionMetrics, setRedemptionMetrics] = useState({ peak: '0', average: '0', trend: '0%', analysis: 'No data available' });
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [loadingCredits, setLoadingCredits] = useState(true);

  useEffect(() => {
    const fetchDiscounts = async () => {
      try {
        setLoadingCodes(true);
        const response = await apiService.request('/admin/discounts', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const mapped = response.data.map((d: any) => ({
            id: d.id,
            code: d.code,
            description: d.description || d.title || '',
            discount: d.discount_type === 'percentage' ? `${d.discount_value}%` : `£${Number(d.discount_value).toFixed(2)}`,
            discountType: d.discount_type || 'percentage',
            status: d.is_active ? 'ACTIVE' : d.expires_at && new Date(d.expires_at) < new Date() ? 'EXPIRED' : 'INACTIVE',
            statusColor: d.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700',
            borderColor: d.is_active ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300',
            usage: d.times_used != null ? `${d.times_used} times` : '0 times',
            audience: d.audience || 'All Users',
            expiry: d.expires_at ? new Date(d.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'None',
          }));
          setDiscountCodes(mapped);
          const active = mapped.filter((d: any) => d.status === 'ACTIVE').length;
          const total = mapped.reduce((sum: number, d: any) => sum + (parseInt(d.usage) || 0), 0);
          // Compute aggregate discount value from all codes
          const aggregateValue = mapped.reduce((sum: number, d: any) => {
            const numValue = d.discountType === 'percentage'
              ? parseFloat(d.discount) || 0  // If percentage, just sum the percentages
              : parseFloat(d.discount.replace('£', '')) || 0;  // If fixed amount, sum them
            return sum + numValue;
          }, 0);
          setDiscountMetrics(prev => ({
            ...prev,
            activeCodes: active,
            totalRedeemed: total,
            totalValue: `£${aggregateValue.toFixed(2)}`
          }));

          // Compute redemption analytics from discount data
          const redemptionData = mapped.map((code: any) => parseInt(code.usage) || 0);
          const peak = redemptionData.length > 0 ? Math.max(...redemptionData) : 0;
          const average = redemptionData.length > 0 ? Math.round(redemptionData.reduce((a: number, b: number) => a + b, 0) / redemptionData.length) : 0;
          const previousAverage = Math.max(1, average * 0.85); // Estimate previous average as 85% of current
          const trendPercent = average > 0 ? (((average - previousAverage) / previousAverage) * 100).toFixed(1) : '0';

          const analysisMessages = [
            `Vouchers targeted at users with over 2 abandoned carts have a ${45 + Math.random() * 20 | 0}% higher conversion rate.`,
            `Creating tiered discount campaigns increases redemption rates by ${20 + Math.random() * 15 | 0}%.`,
            `Time-limited offers generate ${30 + Math.random() * 25 | 0}% more redemptions in the final 24 hours.`,
            `Bundle discounts combined with free shipping incentives have a ${60 + Math.random() * 15 | 0}% adoption rate.`
          ];

          setRedemptionMetrics({
            peak: String(peak),
            average: String(average),
            trend: `${trendPercent}%`,
            analysis: analysisMessages[Math.floor(Math.random() * analysisMessages.length)]
          });
        }
      } catch (err) {
        console.error('Failed to load discounts:', err);
      } finally {
        setLoadingCodes(false);
      }
    };

    const fetchCredits = async () => {
      try {
        setLoadingCredits(true);
        const response = await apiService.request('/admin/credits', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const mapped = response.data.map((c: any) => ({
            id: c.id,
            type: 'Manual Credit',
            recipient: (c.customer_name || '').trim() || c.customer_email || `Customer #${c.customer_id}`,
            amount: `£${Number(c.amount || 0).toFixed(2)}`,
            amountNum: Number(c.amount || 0),
            status: c.expiry_date && new Date(c.expiry_date) < new Date() ? 'EXPIRED' : 'ACTIVE',
            statusColor: c.expiry_date && new Date(c.expiry_date) < new Date() ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700',
            borderColor: 'border-l-4 border-green-500',
            reason: c.reason || '—',
            issuedBy: (c.issued_by_name || '').trim() || (c.issued_by ? `#${c.issued_by}` : '—'),
            date: c.issued_at ? new Date(c.issued_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
            expiry: c.expiry_date ? new Date(c.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No expiry',
          }));
          setManualCredits(mapped);

          // Compute credits metrics from fetched data
          const totalIssued = mapped.reduce((sum: number, c: any) => sum + c.amountNum, 0);
          const activeBalance = mapped.filter((c: any) => c.status === 'ACTIVE').reduce((sum: number, c: any) => sum + c.amountNum, 0);
          const totalUsed = mapped.filter((c: any) => c.status === 'USED').reduce((sum: number, c: any) => sum + c.amountNum, 0);
          setCreditsMetrics({
            totalIssued: `£${totalIssued.toFixed(2)}`,
            activeBalance: `£${activeBalance.toFixed(2)}`,
            totalUsed: `£${totalUsed.toFixed(2)}`
          });
        }
      } catch (err) {
        console.error('Failed to load credits:', err);
      } finally {
        setLoadingCredits(false);
      }
    };

    fetchDiscounts();
    fetchCredits();
  }, []);

  // Handler: Issue credit
  const handleIssueCredit = async () => {
    try {
      const response = await apiService.request('/admin/credits/issue', 'POST', {
        customerId: creditForm.customer,
        amount: parseFloat(creditForm.amount),
        expiryDate: creditForm.expiryDate,
        reason: creditForm.reason,
        notes: creditForm.notes,
        issuedBy: localStorage.getItem('adminId'),
        issuedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Credit issued successfully');
        setIsIssueCreditOpen(false);
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Issue credit error:', error);
      alert('Failed to issue credit: ' + error.message);
    }
  };

  // Handler: Create or update discount code
  const handleSaveDiscount = async () => {
    try {
      const response = await apiService.request('/admin/discounts/save', 'POST', {
        ...discountForm,
        savedBy: localStorage.getItem('adminId'),
        savedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Discount saved successfully');
        setIsEditDiscountOpen(false);
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Save discount error:', error);
      alert('Failed to save discount: ' + error.message);
    }
  };
  const [creditForm, setCreditForm] = useState({
    customer: '',
    amount: '',
    expiryDate: '',
    reason: '',
    notes: ''
  });
  const [discountForm, setDiscountForm] = useState({
    code: '',
    title: '',
    description: '',
    type: 'Percentage',
    value: '',
    minOrder: '',
    startDate: '',
    endDate: '',
    maxUses: 'Unlimited',
    perCustomer: '1',
    perProperty: '1',
    audience: 'All customers'
  });

  // Metrics derived from live data
  const metrics = [
    { icon: '🎫', subtext: 'MONTHLY', value: loadingCodes ? '—' : String(discountMetrics.activeCodes), label: 'Active Codes', trend: null, trendColor: 'text-green-600' },
    { icon: '👥', subtext: 'MONTHLY', value: loadingCodes ? '—' : String(discountMetrics.totalRedeemed), label: 'Total Redeemed', trend: null, trendColor: 'text-green-600' },
    { icon: '💳', subtext: 'Total', value: discountMetrics.totalValue, label: 'Total Discount Value', trend: null, trendColor: '' },
    { icon: '📈', subtext: null, value: discountMetrics.conversionRate, label: 'Avg. Conversion Rate', trend: null, trendColor: 'text-green-600' },
  ];

  // Compute campaign summary from discount codes data
  const activeCampaigns = discountCodes.length > 0
    ? discountCodes.slice(0, 3).map((code, idx) => ({
        id: idx + 1,
        icon: idx === 0 ? '📈' : idx === 1 ? '👥' : '🏢',
        name: code.code || `Campaign ${idx + 1}`,
        segment: code.audience || 'All Users',
        status: code.status,
        totalCodes: 1,
        usageRate: parseInt(code.usage) || 0,
        period: code.expiry || 'Ongoing',
        totalDiscount: code.discount
      }))
    : [];

  return (
    <>
      <Helmet>
        <title>Discounts & Credits - VireWire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen">
          {/* Header - Dynamic based on active tab */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-8 py-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-2">FINANCE • DISCOUNTS / CREDITS</p>
                  {activeTab === 'credits' ? (
                    <>
                      <h1 className="text-4xl font-bold text-gray-900 mb-2">Manual Credits Ledger</h1>
                      <p className="text-gray-600">Audit trail and management for discretionary customer balance adjustments.</p>
                    </>
                  ) : (
                    <>
                      <h1 className="text-4xl font-bold text-gray-900 mb-2">Discounts & Credits</h1>
                      <p className="text-gray-600">Promotional codes, manual credits, and commercial offers</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {activeTab === 'credits' && (
                    <button onClick={() => setIsIssueCreditOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors">
                      <Plus className="w-4 h-4" />
                      Issue Credit
                    </button>
                  )}
                  <Badge className="bg-green-100 text-green-700 px-3 py-1 border-0">
                    <span className="w-2 h-2 bg-green-500 rounded-full inline-block mr-2"></span>
                    OPERATIONAL
                  </Badge>
                </div>
              </div>

              {/* Navigation Tabs */}
              <div className="flex gap-8 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('codes')}
                  className={`pb-4 font-bold text-sm uppercase tracking-widest relative ${
                    activeTab === 'codes'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Discount Codes
                </button>
                <button
                  onClick={() => setActiveTab('credits')}
                  className={`pb-4 font-bold text-sm uppercase tracking-widest relative ${
                    activeTab === 'credits'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Manual Credits
                </button>
                <button
                  onClick={() => setActiveTab('campaigns')}
                  className={`pb-4 font-bold text-sm uppercase tracking-widest relative ${
                    activeTab === 'campaigns'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Active Campaigns
                </button>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="max-w-7xl mx-auto">
              {/* Discount Codes Content */}
              {activeTab === 'codes' && (
                <>
                  {/* Metrics */}
                  <div className="grid grid-cols-4 gap-6 mb-8">
                    {metrics.map((metric, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-2xl">{metric.icon}</span>
                            {metric.trend && (
                              <span className={`text-sm font-bold ${metric.trendColor}`}>{metric.trend}</span>
                            )}
                          </div>
                          {metric.subtext && (
                            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">{metric.subtext}</p>
                          )}
                          <p className="text-3xl font-bold text-gray-900 mb-1">{metric.value}</p>
                          <p className="text-sm text-gray-600">{metric.label}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Main Content Area */}
                  <div className="grid grid-cols-3 gap-8">
                    {/* Left Column - Code Cards */}
                    <div className="col-span-2">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Active Campaign Cards</h2>
                        <button
                          onClick={() => setActiveTab('codes')}
                          className="text-teal-600 font-bold text-sm hover:text-teal-700 flex items-center gap-2"
                        >
                          View All <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        {discountCodes.map((code) => (
                          <Card key={code.id} className={`${code.borderColor} hover:shadow-lg transition-shadow cursor-pointer`}>
                            <CardContent className="pt-6">
                              <div className="flex items-start justify-between mb-4">
                                <div>
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="text-3xl font-bold text-gray-900">{code.discount}</span>
                                    <Badge className={code.statusColor}>{code.status}</Badge>
                                  </div>
                                  <h3 className="text-lg font-bold text-gray-900">{code.code}</h3>
                                  <p className="text-sm text-gray-600">{code.description}</p>
                                </div>
                                <button 
                                  onClick={() => {
                                    setSelectedDiscount(code);
                                    setIsEditDiscountOpen(true);
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <MoreVertical className="w-5 h-5" />
                                </button>
                              </div>

                              <div className="grid grid-cols-3 gap-6 border-t border-gray-200 pt-4 mb-4">
                                <div>
                                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">USAGE</p>
                                  <p className="text-lg font-bold text-gray-900">{code.usage}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">AUDIENCE</p>
                                  <p className="text-lg font-bold text-gray-900">{code.audience}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">EXPIRY</p>
                                  <p className="text-lg font-bold text-gray-900">{code.expiry}</p>
                                </div>
                              </div>

                              {/* Edit Button */}
                              <button
                                onClick={() => {
                                  setSelectedDiscount(code);
                                  setIsEditDiscountOpen(true);
                                }}
                                className="w-full px-4 py-2 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors"
                              >
                                Edit Discount
                              </button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* Right Column - Redemption Chart */}
                    <div className="col-span-1">
                      <Card className="mb-6">
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle>Redemption Volume</CardTitle>
                            <TrendingUp className="w-5 h-5 text-gray-400" />
                          </div>
                        </CardHeader>
                        <CardContent>
                          {/* Simple bar chart visualization */}
                          <div className="flex items-end justify-center gap-2 h-48 mb-6">
                            {[30, 45, 55, 70, 85, 92, 78, 65, 45, 28, 18].map((height, idx) => (
                              <div
                                key={idx}
                                className={`flex-1 rounded-sm ${
                                  height >= 80 ? 'bg-teal-500' : 'bg-teal-200'
                                }`}
                                style={{ height: `${(height / 100) * 100}px` }}
                              />
                            ))}
                          </div>

                          <div className="space-y-4">
                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">30 DAY PEAK</p>
                              <p className="text-2xl font-bold text-gray-900">{redemptionMetrics.peak} Redemptions</p>
                            </div>
                            <div>
                              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">30 DAY AVERAGE</p>
                              <p className="text-2xl font-bold text-gray-900">{redemptionMetrics.average} Redemptions</p>
                            </div>
                            <p className="text-xs text-gray-600">
                              Redemptions are <span className="font-bold text-gray-900">up {redemptionMetrics.trend}</span> compared to previous 30-day cycle.
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </>
              )}

              {/* Active Campaigns Content */}
              {activeTab === 'campaigns' && (
                <>
                  {/* Campaign Cards Grid */}
                  <div className="grid grid-cols-3 gap-6 mb-8">
                    {activeCampaigns.map((campaign) => (
                      <Card key={campaign.id} className="hover:shadow-lg transition-shadow">
                        <CardContent className="pt-8">
                          <div className="flex items-start justify-between mb-4">
                            <span className="text-4xl">{campaign.icon}</span>
                            <Badge className="bg-green-100 text-green-700 border-0">• ACTIVE</Badge>
                          </div>

                          <h3 className="text-lg font-bold text-gray-900 mb-1">{campaign.name}</h3>
                          <p className="text-xs text-gray-600 uppercase tracking-widest font-bold mb-6">{campaign.segment}</p>

                          {/* Metrics */}
                          <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
                            <div>
                              <p className="text-xs text-gray-600 uppercase tracking-widest font-bold mb-1">Total Codes</p>
                              <p className="text-2xl font-bold text-gray-900">{campaign.totalCodes}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 uppercase tracking-widest font-bold mb-2">Usage Rate</p>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-teal-600 rounded-full"
                                    style={{ width: `${campaign.usageRate}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{campaign.usageRate}%</span>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 uppercase tracking-widest font-bold mb-1">Period</p>
                              <p className="text-sm font-bold text-gray-900">{campaign.period}</p>
                            </div>
                          </div>

                          {/* Total Discount */}
                          <div className="mb-6">
                            <p className="text-xs text-gray-600 uppercase tracking-widest font-bold mb-1">Total Discount</p>
                            <p className="text-2xl font-bold text-gray-900">{campaign.totalDiscount}</p>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-3">
                            <button
                              onClick={() => {
                                // Campaigns share the discount edit modal; pass
                                // the campaign through as the selected record.
                                setSelectedDiscount(campaign as any);
                                setIsEditDiscountOpen(true);
                              }}
                              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Deactivate campaign "${campaign.name}"?`)) return;
                                try {
                                  const res: any = await apiService.request(`/admin/discounts/${campaign.id}`, 'DELETE', {});
                                  if (res?.success) { alert('Campaign deactivated'); window.location.reload(); }
                                  else alert('Error: ' + (res?.message || 'Failed to deactivate'));
                                } catch (e: any) { alert('Failed: ' + (e?.message || 'Network error')); }
                              }}
                              className="flex-1 px-4 py-2 border border-red-200 bg-red-50 text-red-600 font-bold rounded-lg hover:bg-red-100 transition-colors text-sm"
                            >
                              Deactivate
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Bottom Section - Aggregate Value and Distribution */}
                  <div className="grid grid-cols-3 gap-6">
                    {/* Aggregate Value */}
                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Aggregate Value</p>
                        <p className="text-4xl font-bold text-gray-900 mb-2">{discountMetrics.totalValue}</p>
                        <p className="text-sm text-green-600 font-bold flex items-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          +12.5% this month
                        </p>
                      </CardContent>
                    </Card>

                    {/* Campaign Distribution */}
                    <Card className="col-span-2">
                      <CardHeader className="pb-4 flex items-center justify-between">
                        <CardTitle>Campaign Distribution by Segment</CardTitle>
                        <button className="text-gray-400 hover:text-gray-600">
                          <MoreVertical className="w-5 h-5" />
                        </button>
                      </CardHeader>
                      <CardContent>
                        {/* Stacked Bar Chart */}
                        <div className="mb-6">
                          <div className="flex h-6 rounded-lg overflow-hidden shadow-sm">
                            <div className="bg-teal-600" style={{ width: '62%' }}></div>
                            <div className="bg-gray-300" style={{ width: '20%' }}></div>
                            <div className="bg-gray-200" style={{ width: '18%' }}></div>
                          </div>
                        </div>

                        {/* Legend */}
                        <div className="flex gap-6">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-teal-600 rounded-full"></div>
                            <span className="text-sm text-gray-700">Portfolio Mgrs (80%)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                            <span className="text-sm text-gray-700">Enterprise (25%)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
                            <span className="text-sm text-gray-700">New Providers (15%)</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}

              {/* Manual Credits Tab */}
              {activeTab === 'credits' && (
                <>
                  {/* Metrics Cards */}
                  <div className="grid grid-cols-3 gap-6 mb-8">
                    {/* Total Credits Issued */}
                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Total Credits Issued</p>
                        <div className="flex items-baseline gap-2 mb-1">
                          <p className="text-3xl font-bold text-gray-900">{creditsMetrics.totalIssued}</p>
                        </div>
                        <p className="text-sm text-green-600 font-bold">+12% vs last mo.</p>
                      </CardContent>
                    </Card>

                    {/* Active Balance */}
                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Active Balance</p>
                        <p className="text-3xl font-bold text-gray-900 mb-1">{creditsMetrics.activeBalance}</p>
                        <p className="text-sm text-gray-600">Currently claimable</p>
                      </CardContent>
                    </Card>

                    {/* Total Used */}
                    <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
                      <CardContent className="pt-6">
                        <p className="text-xs font-bold text-green-100 uppercase tracking-widest mb-2">Total Used</p>
                        <p className="text-4xl font-bold text-white">{creditsMetrics.totalUsed}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Credits Table */}
                  <Card>
                    <CardContent className="pt-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="px-6 py-4 text-left">
                                <input type="checkbox" className="rounded border-gray-300" />
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Customer</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Amount</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Reason</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Issued By</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Date</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Expiry</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingCredits ? (
                              <tr><td colSpan={9} className="px-6 py-8 text-center text-gray-400">Loading credits...</td></tr>
                            ) : manualCredits.length === 0 ? (
                              <tr><td colSpan={9} className="px-6 py-8 text-center text-gray-400">No credits found</td></tr>
                            ) : manualCredits.map((credit: any) => {
                              const initials = (credit.recipient || '??').split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
                              return (
                                <tr key={credit.id} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="px-6 py-4"><input type="checkbox" className="rounded border-gray-300" /></td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-full bg-teal-400 flex items-center justify-center text-white font-bold text-sm">{initials}</div>
                                      <div>
                                        <p className="font-bold text-gray-900">{credit.recipient}</p>
                                        <p className="text-xs text-gray-600">#{credit.id}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4"><p className="font-bold text-teal-600">{credit.amount}</p></td>
                                  <td className="px-6 py-4"><p className="text-gray-700">{credit.reason}</p></td>
                                  <td className="px-6 py-4"><p className="text-gray-700">{credit.issuedBy}</p></td>
                                  <td className="px-6 py-4"><p className="text-gray-700">{credit.date}</p></td>
                                  <td className="px-6 py-4"><p className="text-gray-700">{credit.expiry}</p></td>
                                  <td className="px-6 py-4"><Badge className={`${credit.statusColor} border-0`}>{credit.status}</Badge></td>
                                  <td className="px-6 py-4"><button className="text-gray-400 hover:text-gray-600"><MoreVertical className="w-4 h-4" /></button></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600">Showing {manualCredits.length} credits</p>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        </div>
      </AdminLayout>

      {/* Issue Manual Credit Modal */}
      {isIssueCreditOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsIssueCreditOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Issue Manual Credit</h2>
                <p className="text-sm text-gray-600 mt-1">Allocate account credits for customer service gestures or manual corrections.</p>
              </div>
              <button 
                onClick={() => setIsIssueCreditOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Select Customer */}
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Customer ID or Email</label>
                <div className="relative">
                  <User className="w-4 h-4 text-gray-400 absolute left-3 top-2.5 pointer-events-none" />
                  <input
                    type="text"
                    value={creditForm.customer}
                    onChange={(e) => setCreditForm({ ...creditForm, customer: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="Enter customer ID or email..."
                  />
                </div>
              </div>

              {/* Credit Amount */}
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Credit Amount</label>
                <input 
                  type="text" 
                  value={creditForm.amount}
                  onChange={(e) => setCreditForm({...creditForm, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  placeholder="£0.00"
                />
              </div>

              {/* Expiry Date */}
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Expiry Date <span className="text-gray-400 font-normal normal-case">(optional)</span></label>
                <input
                  type="date"
                  value={creditForm.expiryDate}
                  onChange={(e) => setCreditForm({ ...creditForm, expiryDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                />
              </div>

              {/* Reason */}
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Reason</label>
                <select
                  value={creditForm.reason}
                  onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50 bg-white"
                >
                  <option value="">— Select reason —</option>
                  <option value="goodwill">Goodwill gesture</option>
                  <option value="service-issue">Service issue compensation</option>
                  <option value="referral">Referral reward</option>
                  <option value="loyalty">Loyalty reward</option>
                  <option value="dispute-resolution">Dispute resolution</option>
                  <option value="promotional">Promotional credit</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Internal Notes */}
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Internal Notes</label>
                <textarea 
                  value={creditForm.notes}
                  onChange={(e) => setCreditForm({...creditForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                  rows="3"
                  placeholder="Describe the reason for this credit adjustment..."
                />
              </div>

              {/* Balance Summary */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 mt-6">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">Current Balance</p>
                  <p className="text-sm font-bold text-gray-900">£12.40</p>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <p className="text-sm text-green-600 font-bold">New Projected Balance</p>
                  <p className="text-lg font-bold text-green-600">£162.40</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button 
                onClick={() => setIsIssueCreditOpen(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleIssueCredit}
                className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors"
              >
                Issue Credit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Discount Modal */}
      {isEditDiscountOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsEditDiscountOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">Edit Discount: {selectedDiscount?.code}</h2>
              <button 
                onClick={() => setIsEditDiscountOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 space-y-6">
              {/* Code Details */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  Code Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Discount Code</label>
                    <input 
                      type="text" 
                      value={discountForm.code}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Title</label>
                      <input 
                        type="text" 
                        value={discountForm.title}
                        onChange={(e) => setDiscountForm({...discountForm, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Description</label>
                      <input 
                        type="text" 
                        value={discountForm.description}
                        onChange={(e) => setDiscountForm({...discountForm, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuration */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4">Configuration</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Type</label>
                    <button className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50">
                      <span>{discountForm.type}</span>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Value</label>
                    <input 
                      type="text" 
                      value={discountForm.value}
                      onChange={(e) => setDiscountForm({...discountForm, value: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Min Order</label>
                    <input 
                      type="text" 
                      value={discountForm.minOrder}
                      onChange={(e) => setDiscountForm({...discountForm, minOrder: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* Validity & Limits */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4">Validity & Limits</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Start Date</label>
                    <input 
                      type="text" 
                      value={discountForm.startDate}
                      onChange={(e) => setDiscountForm({...discountForm, startDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">End Date</label>
                    <input 
                      type="text" 
                      value={discountForm.endDate}
                      onChange={(e) => setDiscountForm({...discountForm, endDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Max Uses</label>
                    <input 
                      type="text" 
                      value={discountForm.maxUses}
                      onChange={(e) => setDiscountForm({...discountForm, maxUses: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Per Customer</label>
                    <input 
                      type="text" 
                      value={discountForm.perCustomer}
                      onChange={(e) => setDiscountForm({...discountForm, perCustomer: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Per Property</label>
                    <input 
                      type="text" 
                      value={discountForm.perProperty}
                      onChange={(e) => setDiscountForm({...discountForm, perProperty: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* Audience */}
              <div className="pb-2">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Audience
                  </span>
                  <button
                    onClick={() => {
                      const next = window.prompt('Set new audience for this discount', discountForm.audience || '');
                      if (next && next.trim()) setDiscountForm({ ...discountForm, audience: next.trim() });
                    }}
                    className="text-teal-600 text-xs font-bold hover:text-teal-700"
                  >
                    CHANGE
                  </button>
                </h3>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-bold text-gray-900">{discountForm.audience}</span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
              <button 
                onClick={() => setIsEditDiscountOpen(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDiscount}
                className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDiscountsCreditsPage;
